import { withErrorBoundary } from 'app/components/error-boundary';
import { Column, Row } from 'app/design/primitives/box';
import { Page } from 'app/design/primitives/page';
import { Radio } from 'app/design/primitives/radio';
import { Text } from 'app/design/primitives/text';
import { useNavigationOptions } from 'app/hooks/navigation';
import { getI18nLanguage, languageLabels, supportedLanguages } from 'app/lib/language';
import { useOnePreference, useUpsertOnePreference } from 'app/network/preferences';
import type { NavigationScreen } from 'app/types/navigation';
import type { PreferenceNameToValue } from 'app/types/preferences';
import { usePageTitle, useUISize } from 'app/utils/web-helpers';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, RefreshControl } from 'react-native';

const LanguageScreenComponent: NavigationScreen<'language'> = ({ navigation }) => {
  const { t } = useTranslation('language');
  const languagePreferenceQuery = useOnePreference('language');

  const setPageTitle = usePageTitle('language');
  useEffect(() => setPageTitle('title'), [setPageTitle]);
  useNavigationOptions(
    navigation,
    {
      headerTitle: () => (
        <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
          {t(`title`)}
        </Text>
      ),
    },
    false,
  );

  return (
    <Page
      title={t('title')}
      refreshControl={
        <RefreshControl
          onRefresh={() => void languagePreferenceQuery.refetch()}
          refreshing={languagePreferenceQuery.isRefetching}
        />
      }
    >
      <LanguageScreenAvailables />
    </Page>
  );
};

const LanguageScreenWithErrorBoundary = withErrorBoundary(LanguageScreenComponent);

export const LanguageScreen: NavigationScreen<'language'> = (props) => <LanguageScreenWithErrorBoundary {...props} />;

export const LanguageScreenAvailables = () => {
  const { i18n, t } = useTranslation('language');

  const upsertLanguagePreference = useUpsertOnePreference('language');
  const { isLargeUI } = useUISize();

  const onLanguageChange = async (to: PreferenceNameToValue['language']) => {
    if (getI18nLanguage() === to) return;
    upsertLanguagePreference.mutate(to);
    await i18n.changeLanguage(to);
  };

  return (
    <Column gap="spacing.general.x2">
      {isLargeUI ? (
        <Text fontWeight="700" textAlign="left" variant="bodyLarge">
          {t('title')}
        </Text>
      ) : null}

      <Column
        gap="spacing.general.x4"
        borderWidth={isLargeUI ? 1 : 0}
        borderColor="surface.disabled"
        borderRadius="radius.lg"
        padding={isLargeUI ? 'spacing.general.x4' : undefined}
      >
        {supportedLanguages.map((language) => (
          <Pressable key={language} onPress={() => void onLanguageChange(language)}>
            <Row alignItems="center" flex={1}>
              <Radio checked={getI18nLanguage() === language} onPress={() => void onLanguageChange(language)} />
              <Text fontSize={16} marginStart="spacing.general.x4" variant="bodyBase">
                {languageLabels[language]}
              </Text>
            </Row>
          </Pressable>
        ))}
      </Column>
    </Column>
  );
};
